import React, { memo, useCallback, useState } from 'react';
import { PermissionAction, VerificationDecisionModel, PermissionName } from '@utmost.co/enterprise-models';
import { useLoggedInUser } from 'src/utils/hooks/useLoggedInUser';
import { timeAndExpensesConfig } from '../../finance/timeAndExpenses';
import { DeniedPermissionHelper } from '../permissionHelpers/DeniedPermissionHelper';
import { AuthUser } from 'src/providers/User/AuthUserModels';

// TODO: Bart it should go to permissions folder

interface IResourcePermissionName {
  permissionName: PermissionName;
}
interface IStaticPermissionProps extends IResourcePermissionName {
  self?: AuthUser;
  allowUnsure?: boolean;
}
export type SitePermissionModel = {
  [index in PermissionAction]: boolean;
};

// For classes components
const getStaticPermission = ({ self, permissionName, allowUnsure }: IStaticPermissionProps): SitePermissionModel => {
  const permission = self?.sitePermissions?.[permissionName];
  const decisions: VerificationDecisionModel[] = allowUnsure ? ['PERMIT', 'UNSURE'] : ['PERMIT'];
  return {
    CREATE: decisions.includes(permission?.CREATE ?? 'DENY'),
    DELETE: decisions.includes(permission?.DELETE ?? 'DENY'),
    READ: decisions.includes(permission?.READ ?? 'DENY'),
    UPDATE: decisions.includes(permission?.UPDATE ?? 'DENY'),
    IMPORT: decisions.includes(permission?.IMPORT ?? 'DENY')
  };
};

// For hooks
/*
 * Example of use:
 * const sitePermission = useStaticPermissions();
 * sitePermission({permissionName: 'COMPLIANCE_DEFINITION'}) && "render something"
 * sitePermission({permissionName:'USER_MANAGMENT' }) && "render something"
 */

export type StaticPermissionsFn = (props: IResourcePermissionName, allowUnsure?: boolean) => SitePermissionModel;
export function useStaticPermissions(): StaticPermissionsFn {
  const self = useLoggedInUser();
  return useCallback(
    (props: IResourcePermissionName, allowUnsure?: boolean) => getStaticPermission({ self, ...props, allowUnsure }),
    [self]
  );
}

export function useStaticPermission(permissionName: PermissionName, allowUnsure?: boolean): SitePermissionModel {
  const self = useLoggedInUser();
  return getStaticPermission({ self, permissionName, allowUnsure });
}

export function useStaticPermissionsByName<T extends PermissionName = PermissionName>(
  permissionNames: T[],
  allowUnsure?: boolean
): { [prop in T]: SitePermissionModel } {
  const self = useLoggedInUser();
  const [result] = useState(() =>
    permissionNames.reduce(
      (acc, permissionName) => {
        const resultData = acc;
        resultData[permissionName] = getStaticPermission({ self, permissionName, allowUnsure });
        return resultData;
      },
      {} as { [prop in T]: SitePermissionModel }
    )
  );
  return result;
}

// NOTE:
// This won't work with React Router or Material Ui Tabs use `useStaticPermissions` instead
export const StaticPermission: React.FC<
  React.PropsWithChildren<{ permissionName: PermissionName; permissionAction: PermissionAction }>
> = memo(({ children, permissionName, permissionAction = 'READ' }) => {
  const permissions = useStaticPermissions()({ permissionName });
  const canRead = !!(permissions[permissionAction] && children);
  return (
    <DeniedPermissionHelper canRead={canRead} permissions={[{ name: permissionName, action: permissionAction }]}>
      {children}
    </DeniedPermissionHelper>
  );
});

// use predicate='any' when you want check if at least 1 permissionName is truthy
export const StaticPermissions: React.FC<
  React.PropsWithChildren<{
    permissionName: PermissionName;
    permissionActions: PermissionAction[];
    match: 'any' | 'all';
  }>
> = memo(({ children, permissionName, permissionActions, match }) => {
  const permissions = useStaticPermissions()({ permissionName });
  const permissionsArray: boolean[] = permissionActions.map((action) => permissions[action]);
  const hasPermission =
    match === 'any'
      ? permissionsArray.includes(true)
      : permissionsArray.includes(true) && !permissionsArray.includes(false);
  return hasPermission && children ? children : null;
});

export const ShowIf: React.FC<React.PropsWithChildren<{ condition?: Boolean | boolean }>> = (props) =>
  props.condition && props.children ? props.children : null;

export function isSettingVisible(user: AuthUser) {
  const { sitePermissions } = { ...user };
  return (
    sitePermissions?.SETTINGS_USER?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_ANOMALIES?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_SYSTEM_ACTIVITIES?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_LOCATION?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_CLASSIFICATION?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_PERMISSIONS?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_CUSTOM_FIELDS?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_ENGAGEMENTS?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_CLIENT_IDS?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_SOURCING?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_SMART_FORMS?.READ === 'PERMIT' ||
    sitePermissions?.SETTINGS_SFTP?.READ === 'PERMIT'
  );
}

export function isConfigVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.SETTINGS_CONFIGURATION?.READ ?? []);
}

export function isProcessesDefinitionVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.PROCESS_DEFINITION?.READ ?? []);
}
export function isProcessesInstanceVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.PROCESS?.READ ?? []);
}

export function isTAEVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.[timeAndExpensesConfig.permissionName]?.READ ?? []);
}

export function isSupplierVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.SUPPLIER?.READ ?? []);
}

export function isSourcingVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.JOB_APPLICATION?.READ ?? []);
}

export function isWorkRequestVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.WORK_REQUEST?.READ ?? []);
}

export function isPersonVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.PERSON?.READ ?? []);
}

export function isContractsVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.CONTRACT?.READ ?? []);
}

export function isChartVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.ORG?.READ ?? []);
}

export function isProgramAlertsVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.COMPLIANCE_INSTANCE?.READ ?? []);
}

export function isImportVisible(user: AuthUser) {
  return (
    user?.sitePermissions?.PERSON?.IMPORT === 'PERMIT' ||
    user?.sitePermissions?.PAYMENT?.IMPORT === 'PERMIT' ||
    user?.sitePermissions?.SUPPLIER?.IMPORT === 'PERMIT'
  );
}

export function isInvoicesVisible(user: AuthUser) {
  return ['PERMIT', 'UNSURE'].includes(user?.sitePermissions?.INVOICE.READ ?? []);
}
